import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Grid, Row } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import overlayFactory from 'react-bootstrap-table2-overlay';
import { useTranslation } from 'react-i18next';
import { Warning } from '../../../components/Notifications/Notifications';
import { Copy } from '../../../components/Icons/Icons';
import { Spinner } from '../../../components/Utils/Loaders';
import { VisitStages } from '../../../components/Protocols/Visit/Enums';
import { useReplicateFormToVisits } from './useForms';
import { useVisits } from './useVisits';

export const ReplicateFormToVisitsContainer = ({ protocolId, visitId, formId, onSubmitted, onCancel }) => {
    const { t } = useTranslation();
    const [getVisits, visits, , isVisitsLoading] = useVisits();
    const [replicateForm, isLoading] = useReplicateFormToVisits();
    const [selectedVisitIds, setSelectedVisitIds] = useState([]);
    const fetchStartedRef = useRef(false);

    const availableVisits = visits.filter(v => v.crossVersionId !== parseInt(visitId));

    useEffect(() => {
        if (!fetchStartedRef.current || isVisitsLoading) return;
        if (availableVisits.length === 0) {
            onCancel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisitsLoading]);

    useEffect(() => {
        fetchStartedRef.current = true;
        getVisits(protocolId);
    }, [protocolId]);

    const handleReplicate = async () => {
        if (!selectedVisitIds || selectedVisitIds.length === 0) return;

        const response = await replicateForm(protocolId, visitId, formId, selectedVisitIds);
        if (!response) return;

        const { failures } = response.data;
        if (failures && failures.length > 0) {
            Warning("protocolNotifications.form_ReplicatePartialError");
        }

        onSubmitted();
    };

    const columns = [
        {
            dataField: 'title',
            text: t("protocols.visit.visitName")
        },
        {
            dataField: 'stage',
            text: t("protocols.visit.visitStage"),
            formatter: (cell) => VisitStages().find(x => x.id === cell)?.title ?? cell
        },
    ];

    const selectRow = {
        mode: 'checkbox',
        onSelect: (row, isSelect) => {
            if (isSelect)
                setSelectedVisitIds(prev => prev.concat(row.crossVersionId));
            else
                setSelectedVisitIds(prev => prev.filter(id => id !== row.crossVersionId));
        },
        onSelectAll: (isSelect, rows) => {
            setSelectedVisitIds(isSelect ? rows.map(r => r.crossVersionId) : []);
        },
    };

    if (isLoading)
        return <Spinner />;

    return (
        <>
            <Grid fluid>
                <Row>
                    <Col sm={12}>
                        <BootstrapTable
                            classes="table-spaced table-spaced-smallest table-spaced-small"
                            wrapperClasses="table-responsive"
                            bordered={false}
                            hover
                            remote
                            keyField='id'
                            data={availableVisits}
                            columns={columns}
                            noDataIndication={() => <>{t("commons.noData")}</>}
                            loading={isVisitsLoading}
                            overlay={overlayFactory({ spinner: true })}
                            selectRow={selectRow}
                        />
                    </Col>
                </Row>
            </Grid>
            <Grid fluid style={{ marginTop: '10px' }}>
                <Row>
                    <Col sm={12}>
                        <button
                            onClick={handleReplicate}
                            disabled={selectedVisitIds.length === 0}
                            className="btn btn-primary btn-right">
                            <Copy /> {t('protocols.visit.replicateForm_confirm')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-default btn-right"
                            onClick={onCancel}
                            style={{ marginRight: '10px' }}>
                            {t("buttons.cancel")}
                        </button>
                    </Col>
                </Row>
            </Grid>
        </>
    );
};

ReplicateFormToVisitsContainer.propTypes = {
    protocolId: PropTypes.number.isRequired,
    visitId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    formId: PropTypes.number.isRequired,
    onSubmitted: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};
